// type definitions do not use module syntax
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../../../node_modules/@types/gtag.js/index.d.ts"/>

import type { AfterNavigateEvent } from 'src/framework/public/events';

declare global {
	// eslint-disable-next-line no-var
	var dataLayer: unknown[];
}

export class GoogleAnalyticsApi {
	public static SubmissionAction = 'Form Submission';
	private static ApiCallTimeout = 3000;
	private trackingId: string;
	private formInternalName: string;
	private isEmbedded: boolean;

	gtag: Gtag.Gtag = function() {
		// WI: #37281
		// eslint-disable-next-line prefer-rest-params
		return window.dataLayer.push(arguments);
	};

	constructor(trackingId: string, formInternalName: string, isEmbedded: boolean) {
		this.trackingId = trackingId;
		this.formInternalName = formInternalName;
		this.isEmbedded = isEmbedded;

		this.initGoogleAnalyticsApi();
		this.initTracking();
	}

	get pageSuffix() {
		return this.isEmbedded ? this.formInternalName : null;
	}

	initGoogleAnalyticsApi() {
		const script = document.createElement('script');
		script.src = 'https://www.googletagmanager.com/gtag/js?id=' + this.trackingId;
		script.async = true;

		document.head.appendChild(script);

		window.dataLayer = window.dataLayer || [];

		this.gtag('js', new Date());
	}

	initTracking() {
		/**
		 * Set the tag Id
		 * We don't want to automatically send a page view in case the user
		 * has multiple forms embedded with different Gtags on the same page
		 */
		this.gtag('config', this.trackingId, {
			send_page_view: false
		});
	}

	getTrackingUrl(pageFragment?: string): string {
		let result = window.location.pathname;

		if (this.pageSuffix)
			result += '#' + this.pageSuffix;

		if (pageFragment) {
			result += '#' + pageFragment;
		}

		return result;
	}

	recordPageView(pageFragment?: string) : Promise<boolean> {
		return this.sendEvent('page_view', {
			send_to: this.trackingId,
			page_location: this.getTrackingUrl(pageFragment)
		});
	}

	recordNavigation(event: AfterNavigateEvent) : Promise<boolean> {
		const pageTitle = event.data.destinationPage.title;
		const pageNumber = event.data.destinationPage.number;

		// If the page has no title, default to the page number
		const pageFragment = pageTitle || ('Page ' + pageNumber);

		return this.recordPageView(pageFragment);
	}

	recordSubmission(submissionGoalLabel: string, submissionGoalValue: number) : Promise<boolean> {
		return this.sendEvent(GoogleAnalyticsApi.SubmissionAction, {
			send_to: this.trackingId,
			event_category: this.formInternalName,
			event_label: submissionGoalLabel,
			value: submissionGoalValue
		});
	}

	recordConfirmation() : Promise<boolean> {
		// Record the confirmation page after the form is submitted
		return this.recordPageView('confirmation');
	}

	private async sendEvent(eventName: Gtag.EventNames | (string & NonNullable<unknown>), eventParams?: Gtag.ControlParams | Gtag.EventParams | Gtag.CustomParams) : Promise<boolean> {
		let eventSent = false;

		const customCallback = (eventParams as Gtag.ControlParams).event_callback;

		(eventParams as Gtag.ControlParams).event_callback = () => {
			eventSent = true;
			if (customCallback)
				customCallback();
		};

		this.gtag('event', eventName, eventParams);

		return new Promise<boolean>(resolve => {
			let interval: number = 0;

			// Resolve to false if the callback isn't called within the specified time
			const timer = window.setTimeout(() => {
				// If the we reach the timeout, stop checking for the callback to have been called
				clearInterval(interval);

				resolve(false);
			}, GoogleAnalyticsApi.ApiCallTimeout);

			// Check every 100ms for the callback to have been called
			interval = window.setInterval(() => {
				if (eventSent) {
					// If the callback has been called, don't allow the promise to resolve to false
					clearTimeout(timer);

					resolve(eventSent);
				}
			}, 100);
		});
	}
}